<script setup lang="ts">
import { computed } from "vue";
import type { GlossaryCategory, GlossarySubFamily } from "~/data/glossaryData";
import { SUBFAMILY_CONFIGS, ACCENT_COLOR } from "~/data/glossaryData";
import GlossaryIcon from "~/components/ui/GlossaryIcon.vue";

const props = defineProps<{
  category?: GlossaryCategory;
  subFamily?: GlossarySubFamily;
  term?: string;
  imagePath?: string;
}>();

const config = computed(() => {
  if (props.subFamily && SUBFAMILY_CONFIGS[props.subFamily]) {
    return SUBFAMILY_CONFIGS[props.subFamily];
  }
  // Fallback lookup if category was passed as subFamily ID or name
  const subFamilyKey = props.subFamily || (props.category as GlossarySubFamily);
  if (subFamilyKey && SUBFAMILY_CONFIGS[subFamilyKey]) {
    return SUBFAMILY_CONFIGS[subFamilyKey];
  }
  // Default fallback
  return SUBFAMILY_CONFIGS["printmaking-intaglio"];
});

const animationType = computed(() => config.value.animationType);

const resolvedImagePath = computed(() => {
  return props.imagePath || config.value.imagePath;
});

// Color exception for drawing mediums: Charcoal in black, Graphite in grey, others in steelblue
const strokeColor = computed(() => {
  const t = props.term?.toLowerCase() || "";
  if (t.includes("charcoal")) {
    return "#1a1a1a";
  }
  if (t.includes("graphite")) {
    return "#757575";
  }
  return ACCENT_COLOR;
});
</script>

<template>
  <div class="glossary-animation-block w-full select-none">
    <!-- ========================================================= -->
    <!-- 1. STATIC PAINT STROKE TEXTURE ASSETS (PAINT FAMILY)      -->
    <!-- Visible ridged, translucent, or matte paint texture        -->
    <!-- ========================================================= -->
    <div
      v-if="animationType === 'static-image' && resolvedImagePath"
      class="flex flex-col items-start justify-center py-1.5"
    >
      <div class="paint-texture-container relative flex items-center justify-start overflow-hidden rounded-xl bg-white/70 dark:bg-neutral-800/80 p-2.5 border border-neutral-200/50 dark:border-neutral-700/50 shadow-inner max-w-[210px]">
        <img
          :src="resolvedImagePath"
          :alt="term || config.label || 'Paint texture'"
          class="h-12 w-auto max-w-[180px] object-contain select-none pointer-events-none"
          loading="lazy"
        />
      </div>
      <span
        v-if="config.label"
        class="text-[11px] font-medium text-neutral-400 dark:text-neutral-500 mt-1.5 tracking-wide"
      >
        {{ config.label }}
      </span>
    </div>

    <!-- ========================================================= -->
    <!-- 2. DRAWING MEDIUM SINGLE-STROKE ANIMATION                -->
    <!-- Charcoal (black), Graphite (grey), Pastel/Conté/Ink      -->
    <!-- ========================================================= -->
    <div
      v-else-if="animationType === 'drawing-stroke'"
      class="flex flex-col items-start justify-center py-2"
    >
      <div class="drawing-container flex h-12 w-full max-w-[260px] items-center justify-start relative overflow-hidden">
        <svg
          viewBox="0 0 260 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          class="w-full h-8 overflow-visible"
        >
          <!-- Background guide path -->
          <path
            d="M 12 22 C 55 10, 110 32, 160 18 C 195 8, 225 28, 248 20"
            :stroke="strokeColor"
            stroke-width="6"
            stroke-linecap="round"
            stroke-linejoin="round"
            opacity="0.12"
          />
          <!-- Main animated gestural stroke -->
          <path
            d="M 12 22 C 55 10, 110 32, 160 18 C 195 8, 225 28, 248 20"
            :stroke="strokeColor"
            stroke-width="5"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="drawing-main-path"
          />
          <!-- Texture secondary stroke -->
          <path
            d="M 15 25 C 57 13, 112 34, 162 20 C 197 10, 227 30, 245 22"
            :stroke="strokeColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="drawing-texture-path"
          />
        </svg>
      </div>
      <span class="text-[11px] font-medium text-neutral-400 dark:text-neutral-500 mt-1 tracking-wide">
        {{ config.label || "Direct mark-making" }}
      </span>
    </div>

    <!-- ========================================================= -->
    <!-- 3. 3-STAGE PROCESS TIMELINE & SWEEP (ALL OTHER FAMILIES) -->
    <!-- 3-icon row (44px) + 3s progress sweep track               -->
    <!-- ========================================================= -->
    <div v-else class="flex flex-col items-start gap-2.5 pt-1">
      <!-- 3 Stage Columns: Icon Card + Text Label -->
      <div class="stage-group relative flex w-[210px] items-start justify-between">
        <div
          v-for="(iconName, idx) in config.icons"
          :key="iconName + idx"
          class="stage-col flex flex-col items-center gap-1.5 w-14"
        >
          <!-- 44px Rounded Icon Square -->
          <div
            class="stage-box flex h-11 w-11 items-center justify-center rounded-[10px] bg-white dark:bg-neutral-700/80 shadow-[0_1px_3px_rgba(0,0,0,0.06)] border border-neutral-200/70 dark:border-neutral-600/60 transition-all duration-300"
            :class="`stage-box-${idx + 1}`"
          >
            <GlossaryIcon
              :name="iconName"
              :size="19"
              :stroke-width="1.8"
              class="stage-icon text-neutral-400 dark:text-neutral-300 transition-colors duration-300"
            />
          </div>

          <!-- Stage text label -->
          <span
            v-if="config.stageLabels && config.stageLabels[idx]"
            class="stage-label text-[11px] font-medium text-neutral-400 dark:text-neutral-400 leading-tight transition-colors duration-300 text-center"
            :class="`stage-label-${idx + 1}`"
          >
            {{ config.stageLabels[idx] }}
          </span>
        </div>
      </div>

      <!-- Clean Connected Timeline Bar aligned directly under the 3 icons -->
      <div class="timeline-track relative h-[3px] w-[210px] overflow-hidden rounded-full bg-neutral-200/80 dark:bg-neutral-700/80">
        <div class="timeline-sweep absolute top-0 bottom-0 rounded-full" />
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ----------------------------------------------------
   3-Stage Timeline Progress Sweep (3.0s left-to-right loop)
   ---------------------------------------------------- */
.timeline-sweep {
  background-color: #4682B4;
  box-shadow: 0 0 8px rgba(70, 130, 180, 0.7);
  animation: timelineSweep 3s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

@keyframes timelineSweep {
  0% {
    left: 0px;
    width: 56px;
    opacity: 0;
  }
  6% {
    left: 0px;
    width: 56px;
    opacity: 1;
  }
  28% {
    left: 0px;
    width: 56px;
    opacity: 1;
  }
  36% {
    left: 77px;
    width: 56px;
    opacity: 1;
  }
  62% {
    left: 77px;
    width: 56px;
    opacity: 1;
  }
  68% {
    left: 154px;
    width: 56px;
    opacity: 1;
  }
  92% {
    left: 154px;
    width: 56px;
    opacity: 1;
  }
  98%, 100% {
    left: 154px;
    width: 56px;
    opacity: 0;
  }
}

/* Stage 1 Glow */
.stage-box-1 {
  animation: stageGlow1 3s ease-in-out infinite;
}
.stage-label-1 {
  animation: labelGlow1 3s ease-in-out infinite;
}

@keyframes stageGlow1 {
  0%, 100% {
    background-color: #ffffff;
    border-color: rgba(229, 231, 235, 0.7);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    transform: scale(1);
  }
  6%, 28% {
    background-color: #e9f0f6;
    border-color: #4682B4;
    box-shadow: 0 0 12px rgba(70, 130, 180, 0.35);
    transform: scale(1.04);
  }
  34% {
    background-color: #ffffff;
    border-color: rgba(229, 231, 235, 0.7);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    transform: scale(1);
  }
}

@keyframes labelGlow1 {
  0%, 100% { color: #888888; }
  6%, 28% { color: #4682B4; font-weight: 600; }
  34% { color: #888888; }
}

/* Stage 2 Glow */
.stage-box-2 {
  animation: stageGlow2 3s ease-in-out infinite;
}
.stage-label-2 {
  animation: labelGlow2 3s ease-in-out infinite;
}

@keyframes stageGlow2 {
  0%, 30%, 100% {
    background-color: #ffffff;
    border-color: rgba(229, 231, 235, 0.7);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    transform: scale(1);
  }
  36%, 62% {
    background-color: #e9f0f6;
    border-color: #4682B4;
    box-shadow: 0 0 12px rgba(70, 130, 180, 0.35);
    transform: scale(1.04);
  }
  66% {
    background-color: #ffffff;
    border-color: rgba(229, 231, 235, 0.7);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    transform: scale(1);
  }
}

@keyframes labelGlow2 {
  0%, 30%, 100% { color: #888888; }
  36%, 62% { color: #4682B4; font-weight: 600; }
  66% { color: #888888; }
}

/* Stage 3 Glow */
.stage-box-3 {
  animation: stageGlow3 3s ease-in-out infinite;
}
.stage-label-3 {
  animation: labelGlow3 3s ease-in-out infinite;
}

@keyframes stageGlow3 {
  0%, 64%, 100% {
    background-color: #ffffff;
    border-color: rgba(229, 231, 235, 0.7);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    transform: scale(1);
  }
  68%, 92% {
    background-color: #e9f0f6;
    border-color: #4682B4;
    box-shadow: 0 0 12px rgba(70, 130, 180, 0.35);
    transform: scale(1.04);
  }
  98% {
    background-color: #ffffff;
    border-color: rgba(229, 231, 235, 0.7);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    transform: scale(1);
  }
}

@keyframes labelGlow3 {
  0%, 64%, 100% { color: #888888; }
  68%, 92% { color: #4682B4; font-weight: 600; }
  98% { color: #888888; }
}

/* ----------------------------------------------------
   Drawing Medium Stroke Animations
   ---------------------------------------------------- */
.drawing-main-path {
  stroke-dasharray: 280;
  stroke-dashoffset: 280;
  animation: drawStrokeLine 3.2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

.drawing-texture-path {
  stroke-dasharray: 280;
  stroke-dashoffset: 280;
  opacity: 0.65;
  animation: drawStrokeLine 3.2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

@keyframes drawStrokeLine {
  0% { stroke-dashoffset: 280; opacity: 0; }
  8% { opacity: 1; }
  48%, 80% { stroke-dashoffset: 0; opacity: 1; }
  95%, 100% { stroke-dashoffset: 0; opacity: 0; }
}
</style>
